import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApiKeyService } from "../services/api";
import { useNotification } from "../hooks/useNotification";
import { useConfirm } from "../components/ui/ConfirmProvider";
import { AiOutlineSafety, AiOutlineDelete, AiOutlinePlus, AiOutlineCopy, AiOutlineSearch, AiOutlineUser, AiOutlineFilter, AiOutlineClose, AiOutlineCalendar, AiOutlineClockCircle } from "react-icons/ai";
import { formatDateTime } from "../utils/dateUtils";

const ApiKeyAdmin = () => {
    const navigate = useNavigate();
    const apiKeyService = useApiKeyService();
    const notify = useNotification();
    const confirm = useConfirm();
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newKeyData, setNewKeyData] = useState(null);

    // Form fields
    const [targetUserId, setTargetUserId] = useState("");
    const [description, setDescription] = useState("");
    const [expirationDays, setExpirationDays] = useState(0); // Admin default: Sin expiración

    // Filters
    const [filterUserId, setFilterUserId] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");

    useEffect(() => {
        loadAllKeys();
    }, []);

    const loadAllKeys = async () => {
        setLoading(true);
        try {
            const response = await apiKeyService.listAllApiKeys();
            setKeys(response.data);
        } catch (error) {
            notify.error("Error al cargar todas las API Keys");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateKeyForUser = async (e) => {
        e.preventDefault();
        try {
            const response = await apiKeyService.createApiKeyForUser({
                userId: targetUserId,
                description,
                expirationDays: expirationDays === 0 ? null : expirationDays
            });
            setNewKeyData(response.data);
            notify.success(`API Key generada para ${targetUserId}`);
            loadAllKeys();
        } catch (error) {
            notify.error("Error al generar la API Key");
        }
    };

    const handleRevokeKey = async (id, userId) => {
        const ok = await confirm({
            title: "Revocar API Key",
            message: `¿Estás seguro de revocar la clave de ${userId}?`,
            confirmLabel: "Revocar",
            destructive: true,
        });
        if (!ok) return;

        try {
            await apiKeyService.revokeApiKeyAdmin(id);
            notify.success("API Key revocada");
            loadAllKeys();
        } catch (error) {
            notify.error("Error al revocar la API Key");
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        notify.success("Copiado al portapapeles");
    };

    const formatDate = (dateString) =>
        dateString ? formatDateTime(dateString) : "Nunca";

    const filteredKeys = keys.filter(key => {
        const matchesUser = key.userId.toLowerCase().includes(filterUserId.toLowerCase());
        const matchesStatus =
            filterStatus === "all" ||
            (filterStatus === "active" && !key.revoked) ||
            (filterStatus === "revoked" && key.revoked);
        return matchesUser && matchesStatus;
    });

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.titleContainer}>
                    <AiOutlineSafety style={styles.titleIcon} />
                    <h1 style={styles.title}>Gestión de API Keys</h1>
                </div>
                <div style={styles.headerActions}>
                    <button
                        style={styles.addButton}
                        onClick={() => {
                            setNewKeyData(null);
                            setTargetUserId("");
                            setDescription("");
                            setShowCreateModal(true);
                        }}
                    >
                        <AiOutlinePlus style={{ marginRight: '8px' }} />
                        Nueva Clave de Servicio
                    </button>
                    <button
                        style={styles.backButton}
                        onClick={() => navigate('/')}
                    >
                        <AiOutlineClose style={styles.backIcon} />
                        Cerrar
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div style={styles.filterBar}>
                <div style={styles.filterGroup}>
                    <AiOutlineFilter style={styles.filterIcon} />
                    <span style={styles.filterLabel}>Filtrar por:</span>
                </div>
                <div style={styles.searchBox}>
                    <AiOutlineSearch style={styles.searchIcon} />
                    <input
                        type="text"
                        placeholder="Usuario..."
                        style={styles.searchInput}
                        value={filterUserId}
                        onChange={(e) => setFilterUserId(e.target.value)}
                    />
                </div>
                <select
                    style={styles.statusSelect}
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                >
                    <option value="all">Todos los estados</option>
                    <option value="active">Solo Activas</option>
                    <option value="revoked">Solo Revocadas</option>
                </select>
            </div>

            {loading ? (
                <div style={styles.loading}>Cargando todas las claves...</div>
            ) : (
                <div style={styles.tableCard}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.tableHeadRow}>
                                <th style={styles.th}>Usuario</th>
                                <th style={styles.th}>Descripción</th>
                                <th style={styles.th}>Creada por / el</th>
                                <th style={styles.th}>Último uso</th>
                                <th style={styles.th}>Estado</th>
                                <th style={styles.th}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredKeys.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={styles.emptyCell}>No se encontraron API Keys con los filtros aplicados</td>
                                </tr>
                            ) : (
                                filteredKeys.map(key => (
                                    <tr key={key.id} style={styles.tr}>
                                        <td style={styles.td}>
                                            <div style={styles.userIdContainer}>
                                                <AiOutlineUser style={{ marginRight: '4px', color: '#6B7280' }} />
                                                <strong>{key.userId}</strong>
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.keyDescription}>{key.description}</div>
                                            <div style={styles.keyPreview}>{key.keyPreview}</div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.createdBy}>{key.createdBy}</div>
                                            <div style={styles.createdAt}>{formatDate(key.createdAt)}</div>
                                        </td>
                                        <td style={styles.td}>{formatDate(key.lastUsedAt)}</td>
                                        <td style={styles.td}>
                                            <span style={key.revoked ? styles.revokedBadge : styles.activeBadge}>
                                                {key.revoked ? "Revocada" : "Activa"}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            {!key.revoked && (
                                                <button
                                                    style={styles.revokeButton}
                                                    onClick={() => handleRevokeKey(key.id, key.userId)}
                                                    title="Revocar clave"
                                                >
                                                    <AiOutlineDelete />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal de Creación */}
            {showCreateModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modal}>
                        <div style={styles.modalHeader}>
                            <h2>{newKeyData ? "API Key Generada" : "Crear Clave de Servicio"}</h2>
                            <button style={styles.closeButton} onClick={() => setShowCreateModal(false)}>×</button>
                        </div>

                        {!newKeyData ? (
                            <form onSubmit={handleCreateKeyForUser} style={styles.form}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Usuario Destino (ID)</label>
                                    <input
                                        type="text"
                                        placeholder="Ej: g-importador-automatico / jdoe"
                                        style={styles.input}
                                        value={targetUserId}
                                        onChange={(e) => setTargetUserId(e.target.value)}
                                        required
                                    />
                                    <span style={styles.hint}>Identificador del sistema o usuario que usará la clave.</span>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Descripción</label>
                                    <input
                                        type="text"
                                        placeholder="Ej: CI/CD Pipeline / Script de Importación Masiva"
                                        style={styles.input}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        required
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Expiración</label>
                                    <select
                                        style={styles.select}
                                        value={expirationDays}
                                        onChange={(e) => setExpirationDays(parseInt(e.target.value))}
                                    >
                                        <option value={0}>Sin expiración</option>
                                        <option value={30}>30 días</option>
                                        <option value={90}>90 días</option>
                                        <option value={365}>1 año</option>
                                    </select>
                                </div>
                                <div style={styles.modalFooter}>
                                    <button type="button" style={styles.cancelButton} onClick={() => setShowCreateModal(false)}>Cancelar</button>
                                    <button type="submit" style={styles.submitButton}>Crear Clave</button>
                                </div>
                            </form>
                        ) : (
                            <div style={styles.successContent}>
                                <div style={styles.warningAlert}>
                                    <strong>⚠️ ATENCIÓN:</strong> Envía esta clave al usuario de forma segura.
                                    No volverá a mostrarse en el sistema.
                                </div>

                                <div style={styles.keyDisplay}>
                                    <code style={styles.keyCode}>{newKeyData.apiKey}</code>
                                    <button
                                        style={styles.copyButton}
                                        onClick={() => copyToClipboard(newKeyData.apiKey)}
                                        title="Copiar al portapapeles"
                                    >
                                        <AiOutlineCopy /> Copiar
                                    </button>
                                </div>

                                <div style={styles.keyDetails}>
                                    <div style={styles.detailItem}>
                                        <AiOutlineUser style={styles.detailIcon} />
                                        <span>Destinatario: {newKeyData.userId}</span>
                                    </div>
                                    <div style={styles.detailItem}>
                                        <AiOutlineCalendar style={styles.detailIcon} />
                                        <span>Expira: {formatDate(newKeyData.expiresAt)}</span>
                                    </div>
                                    <div style={styles.detailItem}>
                                        <AiOutlineClockCircle style={styles.detailIcon} />
                                        <span>Descripción: {newKeyData.description}</span>
                                    </div>
                                </div>

                                <button
                                    style={styles.doneButton}
                                    onClick={() => setShowCreateModal(false)}
                                >
                                    Cerrar y Finalizar
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    // Reutilizando la mayoría de los estilos de MyApiKeys para consistencia
    container: { padding: "40px", backgroundColor: "#F9FAFB", minHeight: "100%" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" },
    titleContainer: { display: "flex", alignItems: "center", gap: "12px" },
    titleIcon: { fontSize: "32px", color: "#00468B" },
    title: { fontSize: "28px", margin: 0 },
    headerActions: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px 16px',
        backgroundColor: '#6B7280',
        color: '#FFFFFF',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        gap: '8px',
        fontSize: '14px',
        fontWeight: '600',
        transition: 'all 0.2s ease',
    },
    backIcon: {
        fontSize: '16px',
    },
    addButton: { backgroundColor: "#00468B", color: "white", border: "none", padding: "10px 20px", borderRadius: "6px", fontSize: "14px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center" },

    // Filtros
    filterBar: { display: "flex", alignItems: "center", gap: "15px", marginBottom: "20px", backgroundColor: "white", padding: "15px", borderRadius: "8px", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" },
    filterGroup: { display: "flex", alignItems: "center", gap: "8px", color: "#6B7280", fontSize: "14px" },
    filterIcon: { fontSize: "18px" },
    filterLabel: { fontWeight: "500" },
    searchBox: { position: "relative", flex: 1 },
    searchIcon: { position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#9CA3AF" },
    searchInput: { width: "100%", padding: "8px 12px 8px 35px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "14px" },
    statusSelect: { padding: "8px 12px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "14px", backgroundColor: "white" },

    tableCard: { backgroundColor: "white", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", overflow: "hidden" },
    table: { width: "100%", borderCollapse: "collapse", textAlign: "left" },
    tableHeadRow: { backgroundColor: "#F9FAFB", borderBottom: "1px solid #E5E7EB" },
    th: { padding: "12px 16px", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" },
    td: { padding: "16px", borderBottom: "1px solid #E5E7EB", fontSize: "14px" },
    userIdContainer: { display: "flex", alignItems: "center", color: "#111827" },
    keyDescription: { fontWeight: "600", color: "#111827" },
    keyPreview: { fontSize: "12px", color: "#9CA3AF", fontFamily: "monospace" },
    createdBy: { fontWeight: "500", color: "#4B5563" },
    createdAt: { fontSize: "12px", color: "#9CA3AF" },
    activeBadge: { backgroundColor: "#DEF7EC", color: "#03543F", padding: "4px 8px", borderRadius: "9999px", fontSize: "12px", fontWeight: "500" },
    revokedBadge: { backgroundColor: "#FDE8E8", color: "#9B1C1C", padding: "4px 8px", borderRadius: "9999px", fontSize: "12px", fontWeight: "500" },
    revokeButton: { background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: "18px" },
    emptyCell: { padding: "40px", textAlign: "center", color: "#9CA3AF" },
    loading: { textAlign: "center", padding: "40px", fontSize: "18px", color: "#6B7280" },

    // Modal
    modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0, 0, 0, 0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 2000 },
    modal: { backgroundColor: "white", borderRadius: "12px", width: "90%", maxWidth: "600px" },
    modalHeader: { padding: "20px", borderBottom: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between", alignItems: "center" },
    closeButton: { background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#9CA3AF" },
    form: { padding: "20px" },
    formGroup: { marginBottom: "15px" },
    label: { display: "block", fontSize: "14px", fontWeight: "600", marginBottom: "8px" },
    input: { width: "100%", padding: "10px", border: "1px solid #D1D5DB", borderRadius: "6px" },
    select: { width: "100%", padding: "10px", border: "1px solid #D1D5DB", borderRadius: "6px" },
    hint: { fontSize: "12px", color: "#6B7280", marginTop: "4px", display: "block" },
    modalFooter: { padding: "20px", borderTop: "1px solid #E5E7EB", display: "flex", justifyContent: "flex-end", gap: "12px" },
    submitButton: { backgroundColor: "#00468B", color: "white", border: "none", padding: "10px 20px", borderRadius: "6px", fontWeight: "600", cursor: "pointer" },
    cancelButton: { backgroundColor: "white", border: "1px solid #D1D5DB", padding: "10px 20px", borderRadius: "6px", cursor: "pointer" },
    successContent: { padding: "20px", textAlign: "center" },
    warningAlert: { backgroundColor: "#FFFBEB", borderLeft: "4px solid #F59E0B", padding: "16px", textAlign: "left", marginBottom: "20px", fontSize: "14px", color: "#92400E" },
    keyDisplay: { display: "flex", alignItems: "stretch", gap: "0", marginBottom: "20px", borderRadius: "6px", overflow: "hidden", border: "1px solid #D1D5DB" },
    keyCode: { flex: 1, padding: "12px", backgroundColor: "#F3F4F6", textAlign: "left", fontFamily: "monospace", fontSize: "14px", color: "#111827", wordBreak: "break-all" },
    copyButton: { backgroundColor: "#00468B", color: "white", border: "none", padding: "0 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontWeight: "600" },
    keyDetails: { textAlign: "left", marginBottom: "20px", fontSize: "14px", color: "#6B7280" },
    detailItem: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" },
    detailIcon: { fontSize: "16px" },
    doneButton: { width: "100%", padding: "12px", backgroundColor: "#10B981", color: "white", border: "none", borderRadius: "6px", fontSize: "16px", fontWeight: "600", cursor: "pointer" }
};

export default ApiKeyAdmin;
